<template>
  <div class="nebTable"
    :id="uniqueId"
  >
    <div class="nebTable-lockedTableWrapper"
      v-if="hasLockedColumns"
      :style="{
        marginLeft: lockedTableMargin
      }"
    >
      <div class="nebTable-table locked">
        <div class="nebTable-table-row header lockedHeaderRow">
          <div class="nebTable-table-row-cell header"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`hidden-header-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`header--${column.key}`"
              :column="column"
              :columnIdx="columnIdx"
            >
              <span>{{ column.label }}</span>
            </slot>
          </div>

          <div class="nebTable-table-row-cell header locked"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`header-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`header--${column.key}`"
              :column="column"
              :columnIdx="columnIdx"
            >
              <span>{{ column.label }}</span>
            </slot>
          </div>
        </div>

        <div class="nebTable-table-row totals lockedFooterRow"
          v-if="totalsAtTop"
        >
          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`hidden-totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>

          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>
        </div>

        <div class="nebTable-table-row body"
          v-for="(row, rowIdx) in rows"
          :key="`body-row-${rowIdx}`"
          :class="`lockedBodyRow--${rowIdx}`"
        >
          <div class="nebTable-table-row-cell body"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`hidden-body-cell-${rowIdx}-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`cell--${column.key}`"
              :column="column"
              :row="row"
              :columnIdx="columnIdx"
              :rowIdx="rowIdx"
              :cellValue="row[column.key]"
            >
              <span>{{ row[column.key] }}</span>
            </slot>
          </div>

          <div class="nebTable-table-row-cell body"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`body-cell-${rowIdx}-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`cell--${column.key}`"
              :column="column"
              :row="row"
              :columnIdx="columnIdx"
              :rowIdx="rowIdx"
              :cellValue="row[column.key]"
            >
              <span>{{ row[column.key] }}</span>
            </slot>
          </div>
        </div>

        <div class="nebTable-table-row totals lockedFooterRow"
          v-if="!totalsAtTop"
        >
          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`hidden-totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>

          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="nebTable-unlockedTableWrapper"
      :style="unlockedTableStyle"
    >
      <div class="nebTable-table unlocked">
        <div class="nebTable-table-row header unlockedHeaderRow">
          <div class="nebTable-table-row-cell header"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`hidden-header-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`header--${column.key}`"
              :column="column"
              :columnIdx="columnIdx"
            >
              <span>{{ column.label }}</span>
            </slot>
          </div>

          <div class="nebTable-table-row-cell header"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`header-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`header--${column.key}`"
              :column="column"
              :columnIdx="columnIdx"
            >
              <span>{{ column.label }}</span>
            </slot>
          </div>
        </div>

        <div class="nebTable-table-row totals unlockedFooterRow"
          v-if="totalsAtTop"
        >
          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`hidden-totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>

          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>
        </div>

        <div class="nebTable-table-row body"
          v-for="(row, rowIdx) in rows"
          :key="`body-row-${rowIdx}`"
          :class="`unlockedBodyRow--${rowIdx}`"
        >
          <div class="nebTable-table-row-cell body"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`hidden-body-cell-${rowIdx}-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`cell--${column.key}`"
              :column="column"
              :row="row"
              :columnIdx="columnIdx"
              :rowIdx="rowIdx"
              :cellValue="row[column.key]"
            >
              <span>{{ row[column.key] }}</span>
            </slot>
          </div>

          <div class="nebTable-table-row-cell body"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`body-cell-${rowIdx}-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <slot :name="`cell--${column.key}`"
              :column="column"
              :row="row"
              :columnIdx="columnIdx"
              :rowIdx="rowIdx"
              :cellValue="row[column.key]"
            >
              <span>{{ row[column.key] }}</span>
            </slot>
          </div>
        </div>

        <div class="nebTable-table-row totals unlockedFooterRow"
          v-if="!totalsAtTop"
        >
          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in lockedColumns"
            :key="`hidden-totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>

          <div class="nebTable-table-row-cell totals"
            v-for="(column, columnIdx) in unlockedColumns"
            :key="`totals-cell-${columnIdx}`"
            :class="[
              column.justify ? column.justify : 'left'
            ]"
          >
            <span>{{ totals[column.key] || '--' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { includes } from 'lodash'

export default {
  name: 'NebTable',

  props: {
    columns: {
      type: Array,
      default: () => []
    },
    rows: {
      type: Array,
      default: () => []
    },
    totals: {
      type: Object,
      default: () => ({})
    },
    /**
     * Array of columns to lock. Should be an array of strings where eaach string is the key of a column. Each column
     * specified here will be fixed in place so that when the table is scroled horizontally, the column will remain
     * visible.
     */
    lockColumns: {
      type: Array,
      default: () => []
    },
    totalsAtTop: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    unlockedTableStyle: {
      width: '100%',
      marginLeft: '0'
    },
    lockedTableMargin: '0',
    uniqueId: `table-${new Date().getTime()}`
  }),

  computed: {
    lockedColumns () {
      return this.columns
        .filter(c => includes(this.lockColumns, c.key))
    },
    unlockedColumns () {
      return this.columns
        .filter(c => !includes(this.lockColumns, c.key))
    },
    hasLockedColumns () { return this.lockColumns.length > 0 }
  },

  methods: {
    setLockedTableMargin () {
      if (!this.hasLockedColumns) { return }

      const wrapper = document.querySelector(`#${this.uniqueId} .nebTable-lockedTableWrapper`)
      const cells = document.querySelectorAll('.nebTable-table-row-cell.header.locked')

      const wrapperWidth = wrapper.getBoundingClientRect().width
      const columnWidth = Array.from(cells).reduce((acc, value) => {
        return acc + value.getBoundingClientRect().width
      }, 0)

      this.lockedTableMargin = `-${wrapperWidth - columnWidth}px`
    },
    setUnlockedTableStyle () {
      // const tableContainer = document.querySelector(`#${this.uniqueId}.nebTable`)
      const lockedTableWrapper = document.querySelector(`#${this.uniqueId} .nebTable-lockedTableWrapper`)

      if (lockedTableWrapper) {
        const lockedCells = document.querySelectorAll('.nebTable-table-row-cell.header.locked')

        const lockedColumnsWidth = Array.from(lockedCells).reduce((acc, value) => {
          return acc + value.getBoundingClientRect().width
        }, 0)

        // this.unlockedTableStyle.width = `${tableContainer.clientWidth - lockedColumnsWidth}px`
        this.unlockedTableStyle.marginLeft = `-${lockedColumnsWidth}px`
      } else {
        this.unlockedTableStyle.width = '100%'
      }
    }
  },

  watch: {
    lockColumns () {
      this.$nextTick(() => {
        this.setUnlockedTableStyle()
        this.setLockedTableMargin()
      })
    },
    columns () {
      this.$nextTick(() => {
        this.setUnlockedTableStyle()
        this.setLockedTableMargin()
      })
    }
  },

  mounted () {
    this.setUnlockedTableStyle()
    this.setLockedTableMargin()
    window.addEventListener('resize', this.setUnlockedTableStyle)
    window.addEventListener('resize', this.setLockedTableMargin)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.setUnlockedTableStyle)
    window.removeEventListener('resize', this.setLockedTableMargin)
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebTable {
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;

  &-lockedTableWrapper,
  &-unlockedTableWrapper {
    box-sizing: border-box;
    flex-shrink: 0;
    flex-grow: 0;
    border-collapse: collapse;
    white-space: nowrap;
    background-color: $white;
  }

  &-lockedTableWrapper {
    overflow: hidden;
    box-shadow: 4px 0 4px 0 rgba(155, 155, 155, 0.4);
    z-index: 2;
  }

  &-unlockedTableWrapper {
    overflow-x: scroll;
    overflow-y: hidden;
  }

  &-table {
    box-sizing: border-box;
    display: table;
    table-layout: fixed;

    &-row {
      box-sizing: border-box;
      overflow: hidden;
      display: table-row;

      &-cell {
        box-sizing: border-box;
        overflow: hidden;
        display: table-cell;

        &.right {
          text-align: right;
        }

        &.header {
          padding: 15px 24px 40px 24px;
          border-top: solid 5px $slate-grey;
          border-bottom: solid 2px $warm-grey;

          span {
            font-family: $defaultFont;
            font-size: 14px;
            color: $slate-grey;
            font-weight: 500;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
          }
        }

        &.body {
          padding: 15px 24px;
          border-bottom: solid 1px $pinkish-grey;
          vertical-align: top;

          span {
            font-family: $defaultFont;
            font-size: 12px;
            color: $slate-grey;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.25;
            letter-spacing: normal;
          }
        }

        &.totals {
          padding: 15px 24px;
          border-bottom: solid 2px $pinkish-grey;
          background-color: $white-four;

          span {
            font-family: $defaultFont;
            font-size: 12px;
            color: $slate-grey;
            line-height: 1.25;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            letter-spacing: normal;
          }
        }
      }
    }
  }
}
</style>
